import React, { useEffect, useRef, useState } from 'react';
import { api, type MarkGroupPrice, type FallAvgift, type KottKostnad } from '../api';
import { Trash2 } from 'lucide-react';
import { RADIUS, TYPE, getTheme, useThemeMode } from '../theme';
import { Badge, Button, Card, CardHeader, Icon, IconButton, Input, Loader, Select } from '../components/ui';

const VILTSLAG = ['Älg', 'Kronhjort', 'Dovhjort', 'Rådjur', 'Vildsvin', 'Björn', 'Räv', 'Hare', 'Fasan', 'Orre', 'Tjäder', 'Ripa', 'Grågås', 'Kanadagås', 'Ejder'];
const KON = ['alla', 'Tjur', 'Ko', 'Hane', 'Hona', 'Kalv', 'Killing', 'Okänt'];
const TYP = ['standard', 'Helskovel', 'Halvskovel', 'Hind', 'Spetshjort', 'Kalv', 'Spjutare', 'Ungtjur', 'Fulltjur', 'Okänt'];

const kr = (n: number) => `${Math.round(n).toLocaleString('sv-SE')} kr`;
/** EK3: en ny rad börjar tom – viltslag och belopp väljs av användaren. */
export const emptyFall = (): FallAvgift => ({ viltslag: '', kon: 'alla', typ: 'standard', kostnad: 0 });
/** Rader utan viltslag sparas inte. */
export const filledFall = (list: FallAvgift[] | null) => (list == null ? null : list.filter(f => f.viltslag.trim() !== ''));

/**
 * Priser per medlemsgrupp på markens Ekonomi-sida. Ett tomt fält ärver markens grundpris.
 * Annonserna (jaktlag, jaktklubb, enskild jakt, jaktpaket) hämtar sina priser härifrån.
 */
export function GroupPrices({ markId, canEdit, focusGroupId, reloadKey }: { markId: string; canEdit: boolean; focusGroupId?: string | null; reloadKey?: unknown }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [groups, setGroups] = useState<MarkGroupPrice[] | null>(null);
  const [err, setErr] = useState('');

  const load = () => api.markGroupPrices(markId).then(setGroups).catch(e => setErr(e instanceof Error ? e.message : 'Kunde inte hämta grupperna'));
  useEffect(() => { load(); }, [markId, reloadKey]); // eslint-disable-line

  return (
    <Card style={{ marginBottom: 16 }}>
      <CardHeader title="Priser per grupp" sub="Grupperna skapas under Medlemmar. Fällavgifter och köttpris sätts per grupp. Tomma fält ärver markens grundpriser längre ner. Annonser för marken hämtar sina priser från gruppen de gäller." />
      {err && <div role="alert" style={{ color: T.danger, fontSize: 14, fontWeight: 600, marginBottom: 10 }}>{err}</div>}
      {!groups && !err && <Loader inline text="Laddar grupper…" />}
      {groups && groups.length === 0 && <div style={{ ...TYPE.small, fontSize: 14, color: T.textMuted }}>Inga grupper ännu. Skapa grupper under Medlemmar för att sätta egna priser.</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {groups?.map(g => (
          <GroupPriceCard key={g.groupId} g={g} canEdit={canEdit} focused={g.groupId === focusGroupId}
            onSaved={fresh => setGroups(list => list?.map(x => (x.groupId === fresh.groupId ? fresh : x)) ?? null)} />
        ))}
      </div>
    </Card>
  );
}

function GroupPriceCard({ g, canEdit, focused, onSaved }: { g: MarkGroupPrice; canEdit: boolean; focused: boolean; onSaved: (g: MarkGroupPrice) => void }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const ref = useRef<HTMLDivElement>(null);
  const [arsavgift, setArsavgift] = useState(g.arsavgift != null ? String(g.arsavgift) : '');
  const [prisPerJakt, setPrisPerJakt] = useState(g.prisPerJakt != null ? String(g.prisPerJakt) : '');
  const [kottIngar, setKottIngar] = useState<'arv' | 'ja' | 'nej'>(g.kottIngar == null ? 'arv' : g.kottIngar ? 'ja' : 'nej');
  const [fall, setFall] = useState<FallAvgift[] | null>(g.fallavgifter);
  const [kott, setKott] = useState<KottKostnad[] | null>(g.kottkostnad);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { if (focused) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, [focused]);

  const own = g.arsavgift != null || g.prisPerJakt != null || g.kottIngar != null || g.fallavgifter != null || g.kottkostnad != null;
  const num = (v: string) => (v.trim() === '' ? null : Math.max(0, Number(v.replace(/\s/g, '').replace(',', '.')) || 0));

  const save = async (reset = false) => {
    setSaving(true); setMsg('');
    try {
      const fresh = await api.upsertMarkGroupPrice(g.markId, g.groupId, reset
        ? { arsavgift: null, prisPerJakt: null, kottIngar: null, fallavgifter: null, kottkostnad: null }
        : { arsavgift: num(arsavgift), prisPerJakt: num(prisPerJakt), kottIngar: kottIngar === 'arv' ? null : kottIngar === 'ja', fallavgifter: filledFall(fall), kottkostnad: kott });
      if (reset) { setArsavgift(''); setPrisPerJakt(''); setKottIngar('arv'); setFall(null); setKott(null); }
      else setFall(f => filledFall(f));
      onSaved(fresh);
      setMsg('Sparat');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Kunde inte spara');
    } finally { setSaving(false); }
  };

  const label: React.CSSProperties = { color: T.text, fontSize: 14, fontWeight: 700 };
  const small: React.CSSProperties = { padding: '10px 10px' };
  const linkBtn: React.CSSProperties = { background: 'none', border: 'none', color: T.accentText, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: '0 4px', minHeight: 44 };
  const rowGrid = (cols: string): React.CSSProperties => ({ display: 'grid', gridTemplateColumns: cols, gap: 6, alignItems: 'center' });
  const e = g.effective;

  return (
    <div ref={ref} id={`grupp-${g.groupId}`} style={{ border: `${focused ? 2 : 1}px solid ${focused ? T.accentFill : T.border}`, borderRadius: RADIUS.md, padding: 16, background: T.surface2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <span aria-hidden style={{ width: 12, height: 12, borderRadius: '50%', background: g.groupColor, flexShrink: 0 }} />
        <span style={{ fontSize: 16, fontWeight: 800, color: T.text }}>{g.groupName}</span>
        {g.isJaktklubb && <Badge tone="accent">Jaktklubb</Badge>}
        <span style={{ fontSize: 13, color: T.textMuted }}>{g.memberCount} {g.memberCount === 1 ? 'medlem' : 'medlemmar'}</span>
        <Badge tone={own ? 'accent' : 'neutral'} style={{ marginLeft: 'auto' }}>{own ? 'Egna priser' : 'Ärver grundpris'}</Badge>
      </div>

      {!canEdit ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 8, fontSize: 14, color: T.text }}>
          <div><span style={label}>Årsavgift</span><div style={{ fontWeight: 700 }}>{kr(e.arsavgift)}</div></div>
          <div><span style={label}>Per jakttillfälle</span><div style={{ fontWeight: 700 }}>{kr(e.prisPerJakt)}</div></div>
          <div><span style={label}>Kött</span><div style={{ fontWeight: 700 }}>{e.kottIngar ? 'Ingår' : 'Ingår inte'}</div></div>
          {e.fallavgifter.length > 0 && <div style={{ gridColumn: '1 / -1' }}><span style={label}>Fällavgifter</span><div>{e.fallavgifter.map(f => `${f.viltslag}${f.kon && f.kon !== 'alla' ? ` (${f.kon})` : ''} ${kr(f.kostnad)}`).join(' · ')}</div></div>}
          {e.kottkostnad.length > 0 && <div style={{ gridColumn: '1 / -1' }}><span style={label}>Köttpriser</span><div>{e.kottkostnad.map(k => `${k.viltslag} ${k.kostnadPerKg} kr/kg`).join(' · ')}</div></div>}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 12, marginBottom: 14 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={label}>Årsavgift (kr/år)</span>
              <Input inputMode="numeric" value={arsavgift} onChange={ev => setArsavgift(ev.target.value)} placeholder={`Grundpris: ${kr(e.arsavgift)}`}  />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={label}>Pris per jakttillfälle (kr)</span>
              <Input inputMode="numeric" value={prisPerJakt} onChange={ev => setPrisPerJakt(ev.target.value)} placeholder={`Grundpris: ${kr(e.prisPerJakt)}`}  />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={label}>Kött ingår</span>
              <Select value={kottIngar} onChange={ev => setKottIngar(ev.target.value as 'arv' | 'ja' | 'nej')} >
                <option value="arv">Som grundpriset</option>
                <option value="ja">Ja</option>
                <option value="nej">Nej</option>
              </Select>
            </label>
          </div>

          {/* Fällavgifter per viltslag */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <span style={label}>Fällavgifter</span>
              {fall == null
                ? <button type="button" style={linkBtn} onClick={() => setFall([])}>Egna fällavgifter för gruppen</button>
                : <button type="button" style={{ ...linkBtn, color: T.textSub }} onClick={() => setFall(null)}>Använd grundpriset</button>}
            </div>
            {fall == null ? (
              e.fallavgifter.length
                ? <div style={{ fontSize: 14, color: T.textSub }}>{e.fallavgifter.map(f => `${f.viltslag}${f.kon && f.kon !== 'alla' ? ` (${f.kon})` : ''} ${kr(f.kostnad)}`).join(' · ')}</div>
                : <button type="button" style={{ ...linkBtn, padding: 0 }} onClick={() => setFall([emptyFall()])} data-testid="fall-add">+ Lägg till fällavgift</button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {fall.map((f, i) => (
                  <div key={i} style={rowGrid('repeat(auto-fit,minmax(110px,1fr)) 44px')}>
                    <Select value={f.viltslag} onChange={ev => setFall(l => l!.map((x, j) => (j === i ? { ...x, viltslag: ev.target.value } : x)))} style={small} aria-label="Viltslag">
                      <option value="" disabled>Välj viltslag</option>
                      {VILTSLAG.map(v => <option key={v}>{v}</option>)}
                    </Select>
                    <Select value={f.kon} onChange={ev => setFall(l => l!.map((x, j) => (j === i ? { ...x, kon: ev.target.value } : x)))} style={small}>{KON.map(v => <option key={v} value={v}>{v === 'alla' ? 'Alla kön' : v}</option>)}</Select>
                    <Select value={f.typ} onChange={ev => setFall(l => l!.map((x, j) => (j === i ? { ...x, typ: ev.target.value } : x)))} style={small}>{TYP.map(v => <option key={v} value={v}>{v === 'standard' ? 'Alla typer' : v}</option>)}</Select>
                    <Input inputMode="numeric" value={f.kostnad ? String(f.kostnad) : ''} placeholder="kr" onChange={ev => setFall(l => l!.map((x, j) => (j === i ? { ...x, kostnad: Number(ev.target.value.replace(/\D/g, '')) || 0 } : x)))} style={small} aria-label="Kostnad i kronor" />
                    <IconButton label="Ta bort fällavgiften" onClick={() => setFall(l => l!.filter((_, j) => j !== i))} style={{ color: T.danger }}><Icon as={Trash2} size={18} /></IconButton>
                  </div>
                ))}
                {fall.length === 0 && <div style={{ fontSize: 14, color: T.textSub }}>Inga fällavgifter för gruppen ännu.</div>}
                <button type="button" style={{ ...linkBtn, alignSelf: 'flex-start' }} onClick={() => setFall(l => [...(l ?? []), emptyFall()])} data-testid="fall-add">+ Lägg till fällavgift</button>
              </div>
            )}
          </div>

          {/* Köttpriser per viltslag */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <span style={label}>Köttpriser</span>
              {kott == null
                ? <button type="button" style={linkBtn} onClick={() => setKott([...e.kottkostnad])}>Egna köttpriser för gruppen</button>
                : <button type="button" style={{ ...linkBtn, color: T.textSub }} onClick={() => setKott(null)}>Använd grundpriset</button>}
            </div>
            {kott == null ? (
              <div style={{ fontSize: 14, color: T.textSub }}>{e.kottkostnad.length ? e.kottkostnad.map(k => `${k.viltslag} ${k.kostnadPerKg} kr/kg`).join(' · ') : 'Inga köttpriser i grundpriset'}</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {kott.map((k, i) => (
                  <div key={i} style={rowGrid('1.4fr 1fr 44px')}>
                    <Select value={k.viltslag} onChange={ev => setKott(l => l!.map((x, j) => (j === i ? { ...x, viltslag: ev.target.value } : x)))} style={small}>{VILTSLAG.map(v => <option key={v}>{v}</option>)}</Select>
                    <Input inputMode="numeric" value={String(k.kostnadPerKg)} onChange={ev => setKott(l => l!.map((x, j) => (j === i ? { ...x, kostnadPerKg: Number(ev.target.value) || 0 } : x)))} style={small} aria-label="Kronor per kilo" />
                    <IconButton label="Ta bort köttpriset" onClick={() => setKott(l => l!.filter((_, j) => j !== i))} style={{ color: T.danger }}><Icon as={Trash2} size={18} /></IconButton>
                  </div>
                ))}
                <button type="button" style={{ ...linkBtn, alignSelf: 'flex-start' }} onClick={() => setKott(l => [...(l ?? []), { viltslag: 'Vildsvin', kostnadPerKg: 0 }])}>+ Lägg till köttpris</button>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button pill loading={saving} onClick={() => save()}>{saving ? 'Sparar…' : `Spara ${g.groupName}`}</Button>
            {own && <Button variant="ghost" disabled={saving} onClick={() => save(true)} style={{ color: T.textSub }}>Återställ till grundpris</Button>}
            {msg && <span role="status" style={{ fontSize: 14, fontWeight: 600, color: msg === 'Sparat' ? T.ok : T.danger }}>{msg}</span>}
          </div>
        </>
      )}
    </div>
  );
}
